import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { toast } from "sonner";
import { Check, ExternalLink, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input, Label, NativeSelect, Textarea } from "@/components/ui/input";
import { ActionDialog } from "@/components/action-dialog";
import { DataTable } from "@/components/data-table";
import { errorText, PageHeader, StatusBadge } from "@/components/common";
import { api } from "@/lib/api";
import { ago, date } from "@/lib/format";
import { label } from "@/lib/labels";
import { Link } from "react-router";

interface DocRow {
  id: string;
  type: string;
  status: string;
  path: string | null;
  issueDate: string | null;
  expiresOn: string | null;
  createdAt: string;
  providerId: string;
  provider: string;
  memberId: string | null;
}

/** Dosya 5 dakikalık bağlantıyla yeni sekmede açılır; her açılış denetim kaydına yazılır */
async function openFile(id: string) {
  const w = window.open("", "_blank", "noopener");
  try {
    const r = await api<{ url: string }>(`/v1/admin/documents/${id}/file`);
    if (w) w.location.href = r.url;
    else window.open(r.url, "_blank", "noopener");
  } catch (e) {
    w?.close();
    toast.error(errorText(e));
  }
}

function Decide({ doc }: { doc: DocRow }) {
  const qc = useQueryClient();
  const [reason, setReason] = React.useState("");
  const [expiresOn, setExpiresOn] = React.useState("");
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-documents"] });
  return (
    <div className="flex justify-end gap-1">
      <Button variant="ghost" size="sm" onClick={() => void openFile(doc.id)} disabled={!doc.path}>
        <ExternalLink /> Aç
      </Button>
      {doc.status === "pending" && (
        <>
          <ActionDialog
            trigger={
              <Button variant="ghost" size="sm">
                <X /> Reddet
              </Button>
            }
            title="Belgeyi reddet"
            description="Gerekçe hizmet verene gönderilir; yeni belge yükleyebilir."
            confirmLabel="Reddet"
            confirmVariant="destructive"
            canSubmit={reason.trim().length >= 3}
            success="Belge reddedildi."
            onSubmit={async (key) => {
              await api(`/v1/admin/documents/${doc.id}/decide`, {
                method: "POST",
                idempotencyKey: key,
                body: { approve: false, reason: reason.trim() },
              });
              await refresh();
            }}
          >
            <div className="grid gap-2">
              <Label htmlFor={`r-${doc.id}`}>Gerekçe</Label>
              <Textarea
                id={`r-${doc.id}`}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
              />
            </div>
          </ActionDialog>
          <ActionDialog
            trigger={
              <Button size="sm">
                <Check /> Onayla
              </Button>
            }
            title={`${label(doc.type)} — onayla`}
            description={
              doc.type === "police_conduct_certificate"
                ? "Onaylanınca 1 yıllık “Poji Verified” rozeti verilir; dosya incelemeden sonra otomatik silinir (K-043)."
                : "Süreli belgelerde (sigorta, lisans) bitiş tarihini girin."
            }
            confirmLabel="Onayla"
            success="Belge onaylandı."
            onSubmit={async (key) => {
              await api(`/v1/admin/documents/${doc.id}/decide`, {
                method: "POST",
                idempotencyKey: key,
                body: { approve: true, ...(expiresOn ? { expiresOn } : {}) },
              });
              await refresh();
            }}
          >
            <div className="grid gap-2">
              <Label htmlFor={`e-${doc.id}`}>Bitiş tarihi (isteğe bağlı)</Label>
              <Input
                id={`e-${doc.id}`}
                type="date"
                value={expiresOn}
                onChange={(e) => setExpiresOn(e.target.value)}
              />
            </div>
          </ActionDialog>
        </>
      )}
    </div>
  );
}

const columns: ColumnDef<DocRow, unknown>[] = [
  { accessorKey: "type", header: "Belge", cell: ({ row }) => label(row.original.type) },
  {
    accessorKey: "provider",
    header: "Hizmet veren",
    cell: ({ row }) => (
      <Link className="text-primary hover:underline" to={`/providers/${row.original.providerId}`}>
        {row.original.provider}
      </Link>
    ),
  },
  {
    accessorKey: "status",
    header: "Durum",
    cell: ({ row }) => <StatusBadge status={row.original.status} />,
  },
  {
    accessorKey: "issueDate",
    header: "Düzenlenme",
    cell: ({ row }) => date(row.original.issueDate),
  },
  { accessorKey: "createdAt", header: "Yüklendi", cell: ({ row }) => ago(row.original.createdAt) },
  { id: "actions", header: "", cell: ({ row }) => <Decide doc={row.original} /> },
];

export function DocumentsPage() {
  const [status, setStatus] = React.useState("pending");
  const q = useQuery({
    queryKey: ["admin-documents", status],
    queryFn: () => api<{ documents: DocRow[] }>(`/v1/admin/documents?status=${status}&limit=100`),
  });
  return (
    <>
      <PageHeader
        title="Doğrulama belgeleri"
        description="Kimlik, sabıka kaydı, sicil, sigorta ve lisanslar (K-039, K-043, K-050)."
        actions={
          <NativeSelect
            aria-label="Durum"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="pending">Bekleyen</option>
            <option value="approved">Onaylanan</option>
            <option value="rejected">Reddedilen</option>
          </NativeSelect>
        }
      />
      <DataTable
        columns={columns}
        data={q.data?.documents}
        isLoading={q.isPending}
        error={q.error}
        onRetry={() => void q.refetch()}
        empty="Bu durumda belge yok."
      />
    </>
  );
}
